import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { marketplaceAPI, notificationsAPI } from '../services/api';
import { requestUnreadCount } from '../services/socket';
import LanguageSelector from './LanguageSelector';
import { formatShortDate } from '../utils/dateFormat';
import '../styles/Header.css';

const Header = () => {
  const { t } = useTranslation();
  const { user, logout, unreadMessages, unreadNotifications, setUnreadNotifications, socketConnected } = useAuth();
  const { getCartCount } = useCart();
  const cartCount = getCartCount();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [pendingMovements, setPendingMovements] = useState(0);
  const [notifDropdownOpen, setNotifDropdownOpen] = useState(false);
  const [dropdownNotifs, setDropdownNotifs] = useState([]);
  const [notifLoading, setNotifLoading] = useState(false);
  const notifRef = useRef(null);
  const [scrolled, setScrolled] = useState(false);

  // Detect scroll for header shadow
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Fetch mouvements en attente
  const fetchMovementsCount = useCallback(async () => {
    if (!user || user.role === 'admin') return;
    try {
      const mvtRes = await marketplaceAPI.getPendingMovementsCount();
      setPendingMovements(mvtRes.data.total || 0);
    } catch (err) { /* silently fail */ }
  }, [user]);

  useEffect(() => {
    fetchMovementsCount();
    const interval = setInterval(fetchMovementsCount, 30000);
    return () => clearInterval(interval);
  }, [fetchMovementsCount]);

  useEffect(() => {
    const delay = location.pathname === '/mouvements' ? 1200 : 500;
    const timer = setTimeout(fetchMovementsCount, delay);
    return () => clearTimeout(timer);
  }, [location.pathname, fetchMovementsCount]);

  useEffect(() => {
    if (socketConnected && user) requestUnreadCount();
  }, [location.pathname, socketConnected, user]);

  // Close notif dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target)) {
        setNotifDropdownOpen(false);
      }
    };
    if (notifDropdownOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [notifDropdownOpen]);

  const toggleNotifDropdown = async () => {
    const opening = !notifDropdownOpen;
    setNotifDropdownOpen(opening);
    if (opening) {
      setNotifLoading(true);
      try {
        const res = await notificationsAPI.getMy();
        setDropdownNotifs(res.data.slice(0, 10));
      } catch (err) { /* silently fail */ }
      setNotifLoading(false);
    }
  };

  const handleNotifClick = async (notif) => {
    if (!notif.is_read) {
      try {
        await notificationsAPI.markAsRead(notif.id);
        setUnreadNotifications(prev => Math.max(0, prev - 1));
      } catch (err) { /* silently fail */ }
    }
    setNotifDropdownOpen(false);
    navigate('/notifications');
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    setSidebarOpen(false);
  };

  const closeSidebar = () => setSidebarOpen(false);

  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const totalBadge = unreadMessages + unreadNotifications;

  return (
    <>
      {/* ============ TOP HEADER ============ */}
      <header className={`header ${scrolled ? 'header--scrolled' : ''}`}>
        <div className="header__inner">
          {/* Left: Hamburger + Logo */}
          <div className="header__left">
            <button
              className={`header__hamburger ${sidebarOpen ? 'open' : ''}`}
              onClick={() => setSidebarOpen(!sidebarOpen)}
              aria-label="Menu"
            >
              <span></span>
              <span></span>
              <span></span>
            </button>

            <Link to="/" className="header__logo">
              <span className="header__logo-text">Main2Main</span>
            </Link>
          </div>

          {/* Right: Icons */}
          <div className="header__right">
            <div className="header__lang">
              <LanguageSelector />
            </div>

            {user && (
              <>
                {/* Notifications */}
                <div className="header__notif" ref={notifRef}>
                  <button className="header__icon" onClick={toggleNotifDropdown} aria-label="Notifications">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                      <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
                    </svg>
                    {totalBadge > 0 && <span className="header__badge">{totalBadge}</span>}
                  </button>

                  {notifDropdownOpen && (
                    <div className="header__dropdown">
                      <div className="header__dropdown-title">{t('notifications.header')}</div>
                      {unreadMessages > 0 && (
                        <Link to="/my-messages" className="header__dropdown-item unread" onClick={() => setNotifDropdownOpen(false)}>
                          <span>💬 {t('notifications.unreadMessages', { count: unreadMessages, plural: unreadMessages > 1 ? 's' : '' })}</span>
                        </Link>
                      )}
                      {notifLoading ? (
                        <div className="header__dropdown-empty">{t('notifications.loadingNotifs')}</div>
                      ) : dropdownNotifs.length === 0 && unreadMessages === 0 ? (
                        <div className="header__dropdown-empty">{t('notifications.noNotifications')}</div>
                      ) : (
                        dropdownNotifs.map(notif => (
                          <div key={notif.id} className={`header__dropdown-item ${!notif.is_read ? 'unread' : ''}`} onClick={() => handleNotifClick(notif)}>
                            <div className="header__dropdown-item-title">{notif.title}</div>
                            <div className="header__dropdown-item-date">{formatShortDate(notif.created_at)}</div>
                          </div>
                        ))
                      )}
                      <Link to="/notifications" className="header__dropdown-footer" onClick={() => setNotifDropdownOpen(false)}>
                        {t('notifications.viewAll')}
                      </Link>
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Cart */}
            <Link to="/cart" className="header__icon header__cart" aria-label={t('nav.cart')}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>
                <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
              </svg>
              {cartCount > 0 && <span className="header__badge">{cartCount}</span>}
            </Link>

            {/* User / Auth */}
            {user ? (
              <Link to="/dashboard" className="header__icon header__user" aria-label="Profile">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                  <circle cx="12" cy="7" r="4"/>
                </svg>
              </Link>
            ) : (
              <Link to="/login" className="header__login-btn">{t('nav.login')}</Link>
            )}
          </div>
        </div>
      </header>

      {/* ============ MOBILE SIDEBAR ============ */}
      {sidebarOpen && <div className="sidebar-overlay" onClick={closeSidebar}></div>}

      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        {/* Sidebar Header */}
        <div className="sidebar__header">
          <Link to="/" className="sidebar__logo" onClick={closeSidebar}>Main2Main</Link>
          <button className="sidebar__close" onClick={closeSidebar}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
          </button>
        </div>

        {/* Sell CTA */}
        {user && user.role !== 'admin' && (
          <div className="sidebar__cta">
            <Link to="/create-product" className="sidebar__sell-btn" onClick={closeSidebar}>
              + {t('nav.sellItem')}
            </Link>
          </div>
        )}

        {/* Navigation */}
        <nav className="sidebar__nav">
          <Link to="/" className={`sidebar__link ${location.pathname === '/' ? 'active' : ''}`} onClick={closeSidebar}>
            <span>🏠</span> {t('nav.home')}
          </Link>

          {user && user.role !== 'admin' && (
            <>
              <div className="sidebar__section">{t('nav.mySpace')}</div>
              <Link to="/dashboard" className={`sidebar__link ${isActive('/dashboard') ? 'active' : ''}`} onClick={closeSidebar}>
                <span>📊</span> {t('nav.dashboard')}
              </Link>
              <Link to="/create-product" className={`sidebar__link ${isActive('/create-product') ? 'active' : ''}`} onClick={closeSidebar}>
                <span>➕</span> {t('nav.sellItem')}
              </Link>
              <Link to="/create-vehicle" className={`sidebar__link ${isActive('/create-vehicle') ? 'active' : ''}`} onClick={closeSidebar}>
                <span>🚗</span> {t('nav.sellVehicle')}
              </Link>
              <Link to="/bulk-import" className={`sidebar__link ${isActive('/bulk-import') ? 'active' : ''}`} onClick={closeSidebar}>
                <span>📑</span> {t('nav.sellMultiple')}
              </Link>
              <Link to="/my-products" className={`sidebar__link ${isActive('/my-products') ? 'active' : ''}`} onClick={closeSidebar}>
                <span>📦</span> {t('nav.myProducts')}
              </Link>
              <Link to="/mouvements" className={`sidebar__link ${isActive('/mouvements') ? 'active' : ''}`} onClick={closeSidebar}>
                <span>🔄</span> {t('nav.movements')}
                {pendingMovements > 0 && <span className="sidebar__badge">{pendingMovements}</span>}
              </Link>
              <Link to="/seller/onboarding" className={`sidebar__link ${isActive('/seller/onboarding') ? 'active' : ''}`} onClick={closeSidebar}>
                <span>🏪</span> {t('nav.sellerAccount')}
              </Link>
              <Link to="/favorites" className={`sidebar__link ${isActive('/favorites') ? 'active' : ''}`} onClick={closeSidebar}>
                <span>❤️</span> {t('nav.favorites')}
              </Link>
              <Link to="/my-messages" className={`sidebar__link ${isActive('/my-messages') ? 'active' : ''}`} onClick={closeSidebar}>
                <span>💬</span> {t('nav.messages')}
                {unreadMessages > 0 && <span className="sidebar__badge">{unreadMessages}</span>}
              </Link>
              <Link to="/notifications" className={`sidebar__link ${isActive('/notifications') ? 'active' : ''}`} onClick={closeSidebar}>
                <span>🔔</span> {t('nav.notifications')}
                {unreadNotifications > 0 && <span className="sidebar__badge">{unreadNotifications}</span>}
              </Link>
            </>
          )}

          {user && user.role === 'admin' && (
            <>
              <div className="sidebar__section sidebar__section--admin">{t('nav.admin')}</div>
              <Link to="/admin" className={`sidebar__link ${location.pathname === '/admin' ? 'active' : ''}`} onClick={closeSidebar}>
                <span>📊</span> {t('nav.adminDashboard')}
              </Link>
              <Link to="/admin/users" className={`sidebar__link ${isActive('/admin/users') ? 'active' : ''}`} onClick={closeSidebar}>
                <span>👥</span> {t('nav.adminUsers')}
              </Link>
              <Link to="/admin/products" className={`sidebar__link ${isActive('/admin/products') ? 'active' : ''}`} onClick={closeSidebar}>
                <span>📦</span> {t('nav.adminProducts')}
              </Link>
              <Link to="/admin/orders" className={`sidebar__link ${isActive('/admin/orders') ? 'active' : ''}`} onClick={closeSidebar}>
                <span>🛒</span> {t('nav.adminOrders')}
              </Link>
              <Link to="/admin/theme" className={`sidebar__link ${isActive('/admin/theme') ? 'active' : ''}`} onClick={closeSidebar}>
                <span>🎨</span> {t('nav.adminTheme')}
              </Link>
              <Link to="/admin/messages" className={`sidebar__link ${isActive('/admin/messages') ? 'active' : ''}`} onClick={closeSidebar}>
                <span>✉️</span> {t('nav.adminMessages')}
              </Link>
              <Link to="/my-messages" className={`sidebar__link ${isActive('/my-messages') ? 'active' : ''}`} onClick={closeSidebar}>
                <span>💬</span> {t('nav.adminConversations')}
                {unreadMessages > 0 && <span className="sidebar__badge">{unreadMessages}</span>}
              </Link>
              <Link to="/admin/conversations" className={`sidebar__link ${isActive('/admin/conversations') ? 'active' : ''}`} onClick={closeSidebar}>
                <span>🔍</span> {t('nav.adminAllConversations')}
              </Link>
              <Link to="/admin/notifications" className={`sidebar__link ${isActive('/admin/notifications') ? 'active' : ''}`} onClick={closeSidebar}>
                <span>📢</span> {t('nav.adminNotifications')}
              </Link>
            </>
          )}

          <Link to="/contact" className={`sidebar__link ${isActive('/contact') ? 'active' : ''}`} onClick={closeSidebar}>
            <span>📧</span> {t('nav.contact')}
          </Link>
        </nav>

        {/* Sidebar Footer */}
        <div className="sidebar__footer">
          {user ? (
            <>
              <div className="sidebar__user">
                <span className="sidebar__avatar">👤</span>
                <div>
                  <div className="sidebar__username">{user.username}</div>
                  {user.role === 'admin' && <span className="sidebar__role">Admin</span>}
                </div>
              </div>
              <button onClick={handleLogout} className="sidebar__logout">{t('nav.logout')}</button>
            </>
          ) : (
            <div className="sidebar__auth">
              <Link to="/login" className="sidebar__auth-btn sidebar__auth-btn--login" onClick={closeSidebar}>{t('nav.login')}</Link>
              <Link to="/register" className="sidebar__auth-btn sidebar__auth-btn--register" onClick={closeSidebar}>{t('nav.register')}</Link>
            </div>
          )}
        </div>
      </aside>
    </>
  );
};

export default Header;
